// ItemGroupTilesBlockRenderer.tsx  (CONSUMER)
// Renders the CMS "Item Group Tiles" block on a consumer custom page.
//
// The block shows tiles that are the CHILDREN of a source category:
//   • tile_source 'parent' (default) → the page's parent-link children
//   • tile_source 'child'            → this page's own child pages
// (mirrors the hub editor + the trade renderer)
//
// Tiles/config come from the block; the actual tile data is fetched from the
// source category's CMS page. Per-tile image overrides (custom_child_tiles) and
// name overrides (tile_name_overrides) are applied here. The tile visual and the
// type-aware navigation mirror CMSConsumerApplicationsGrid.
//
// On a phone the tiles are laid out exactly like the home page item-group tiles
// (CMSConsumerItemGroupsGrid): one full-width tile per row, same height, same
// gap, same 40px label. From the `sm` breakpoint up they use the trade layout.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getCMSPage } from '../../api/cmsApi';
import { ChildTileData } from '../../types/CMSTypes';
import { isConsumerTile } from './consumerItemGroups';
import { getFileServerBase } from './fileServerBase';
import { mapToStandardBucket, sectionAnchorId } from './itemGroupBuckets';

interface ItemGroupTilesBlockRendererProps {
  fields: Record<string, any>;
  // First parent-link category id — the tile source in 'parent' mode.
  pageParentId?: string | null;
  // This page's own category id — the tile source in 'child' mode.
  pageOwnId?: string | null;
  viewMode?: 'live' | 'draft';
  // Per-page tile image overrides (category_id → filename).
  customChildTiles?: Record<string, string> | null;
}

const BASE_MEDIA_URL = `${getFileServerBase()}/media/large/`;

// Tile layout — same as the trade site: gap between tiles and the narrowest a
// tile may get before the row wraps.
const TILE_GAP = 24;
const MIN_TILE = 150;

// Phone layout — the home page tiles' values (CMSConsumerItemGroupsGrid): row gap
// 16px and a 320px tall tile.
const PHONE_TILE_GAP = 16;
const PHONE_TILE_HEIGHT = 320;

const COLUMN_OPTIONS = ['2', '3', '4', '5'];

const TILE_ASPECT_H_OVER_W = 4 / 3;

// Tile label: 40px like the home tiles, shrunk only when the tile is too narrow
// for its longest word (e.g. "Performance") so the label can never be clipped.
const LABEL_MAX_FONT = 40;
const LABEL_MIN_FONT = 16;
const LABEL_SIDE_SPACE = 24; // px kept clear of the tile edges
const LABEL_LONGEST_WORD_EM = 4.2; // approx width of the longest tile word, in em

// Category types that are not meaningful item-group tiles.
const EXCLUDED_TILE_TYPES = new Set(['nav_add_on', 'new_arrival', 'home']);

const getFieldValue = (field: any): any => {
  if (!field) return null;
  return field.value ?? field.default ?? null;
};

// The hub's Display Name is the label shown on the storefront (e.g. "Stools,
// ottomans & benches"); page_title / category_name are the internal names and only
// a fallback for tiles that have no display name.
const tileName = (t: ChildTileData): string =>
  t.display_name || t.page_title || t.category_name || t.item_group_name || 'Untitled';

// Slug for a custom page tile (matches CMSConsumerApplicationsGrid.getCustomSlug).
const getCustomSlug = (t: ChildTileData): string =>
  (t.display_name || t.category_name || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

// Fabric (005) applications carry extra "?application=" params — mirrors the grid.
const applicationMapping: { [key: string]: string[] } = {
  Curtaining: ['Curtaining', 'Dual Purpose'],
  'Dual Purpose': ['Dual Purpose'],
  Upholstery: ['Upholstery', 'Dual Purpose'],
};

// ─── Scroll-to-section (click_action: "scroll") ────────────────────────────────
// Matches the target sections' scrollMarginTop (see CollectionCardsBlockRenderer).
const SECTION_SCROLL_MARGIN_TOP = 120;
const SCROLL_PIN_WATCH_MS = 3000;
const SCROLL_PIN_QUIET_MS = 400;

// Smooth-scroll to the tile's item-group section on the same page (Collection
// Cards renders id="section-<bucket>" anchors). A Filtered Products grid usually
// sits between the tiles and the sections and reflows as it loads, so the target
// keeps shifting for ~1s after the click. Glide once, then PIN the section to the
// top with instant per-frame corrections so late growth above it is absorbed
// silently. Any genuine user scroll (wheel/touch/key) cancels. Mirrors trade.
const scrollToSection = (tile: ChildTileData) => {
  const bucket = mapToStandardBucket(tile.item_group, tile.item_group_name, tile.category_name);
  const el = document.getElementById(sectionAnchorId(bucket));
  if (!el) return;

  const targetY = () =>
    Math.max(0, el.getBoundingClientRect().top + window.scrollY - SECTION_SCROLL_MARGIN_TOP);

  let cancelled = false;
  let raf = 0;
  const cleanup = () => {
    cancelled = true;
    cancelAnimationFrame(raf);
    window.removeEventListener('wheel', onUserScroll);
    window.removeEventListener('touchmove', onUserScroll);
    window.removeEventListener('keydown', onUserScroll);
  };
  const onUserScroll = () => cleanup();
  window.addEventListener('wheel', onUserScroll, { passive: true });
  window.addEventListener('touchmove', onUserScroll, { passive: true });
  window.addEventListener('keydown', onUserScroll);

  const startY = window.scrollY;
  window.scrollTo({ top: targetY(), behavior: 'smooth' });

  const deadline = Date.now() + SCROLL_PIN_WATCH_MS;
  let arrived = false;
  let hasMoved = false;
  let prevY = startY;
  let quietSince = 0;

  const tick = () => {
    if (cancelled) return;
    const y = window.scrollY;
    const want = targetY();

    if (!arrived) {
      if (Math.abs(y - startY) > 8) hasMoved = true;
      if (Math.abs(y - want) <= 4 || (hasMoved && Math.abs(y - prevY) < 1)) {
        arrived = true;
      }
    }

    if (arrived) {
      if (Math.abs(y - want) > 1) {
        window.scrollTo({ top: want, behavior: 'auto' });
        quietSince = 0;
      } else if (!quietSince) {
        quietSince = Date.now();
      }
      if (quietSince && Date.now() - quietSince > SCROLL_PIN_QUIET_MS) {
        cleanup();
        return;
      }
    }

    prevY = window.scrollY;
    if (Date.now() < deadline) {
      raf = requestAnimationFrame(tick);
    } else {
      cleanup();
    }
  };
  raf = requestAnimationFrame(tick);
};

const ItemGroupTilesBlockRenderer: React.FC<ItemGroupTilesBlockRendererProps> = ({
  fields,
  pageParentId = null,
  pageOwnId = null,
  viewMode = 'live',
  customChildTiles = null,
}) => {
  const navigate = useNavigate();

  const autoDisplay: boolean = getFieldValue(fields.auto_display) ?? false;
  // 'scroll' → jump to the item group's Collection Cards section on this page;
  // 'navigate' (default) → route through to the item group / application page.
  const clickAction: string = getFieldValue(fields.click_action) ?? 'navigate';
  const selectedIds: string[] = getFieldValue(fields.selected_tile_ids) ?? [];
  const nameOverrides: Record<string, string> = getFieldValue(fields.tile_name_overrides) ?? {};
  const overrideSourceId: string | null = getFieldValue(fields.source_category_id) ?? null;
  const maxWidth: string = getFieldValue(fields.max_width) ?? '';
  // Where the tiles come from: 'parent' (parent-link children, default) or 'child'
  // (this page's own children). Set in hub on the Item Group Tiles block.
  const tileSource: string = getFieldValue(fields.tile_source) ?? 'parent';
  const rawColumns = String(getFieldValue(fields.columns) ?? 'default');
  const chosenCols: number | null = COLUMN_OPTIONS.includes(rawColumns) ? Number(rawColumns) : null;

  // An explicit source override always wins; otherwise child mode sources this
  // page's own children, and parent mode (default) the parent-link children.
  const sourceId = overrideSourceId || (tileSource === 'child' ? pageOwnId : pageParentId) || null;

  const [pool, setPool] = useState<ChildTileData[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!sourceId) {
      setPool([]);
      return;
    }
    (async () => {
      try {
        const onlyPublished = viewMode !== 'draft';
        const res = await getCMSPage(sourceId, onlyPublished);
        const ctd =
          (res as any)?.cms_page?.children_tile_data ??
          (res as any)?.cms_page_shadow?.children_tile_data;
        const list: ChildTileData[] =
          (viewMode === 'draft' ? ctd?.shadow : ctd?.active) ?? ctd?.active ?? [];
        if (!cancelled) {
          setPool(
            Array.isArray(list)
              ? list.filter(
                  (t) =>
                    t &&
                    t.category_id &&
                    t.category_id !== pageOwnId && // never offer the page itself
                    !EXCLUDED_TILE_TYPES.has(t.category_type ?? '') &&
                    // Consumer only sells Fabric (005) / Wallpaper (001); drop the
                    // Rugs/Furniture/Homeware tiles (Homeware is a "group" tile
                    // with no item_group, so match by name too). Custom pages with
                    // no item group (e.g. Performance Fabric) are kept.
                    isConsumerTile(t)
                )
              : []
          );
        }
      } catch (e) {
        console.error('ItemGroupTiles (consumer): failed to load tile pool', e);
        if (!cancelled) setPool([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceId, viewMode, pageOwnId]);

  const byId = useMemo(
    () => Object.fromEntries(pool.map((t) => [t.category_id, t])) as Record<string, ChildTileData>,
    [pool]
  );
  const sortedPool = useMemo(
    () => [...pool].sort((a, b) => tileName(a).toLowerCase().localeCompare(tileName(b).toLowerCase())),
    [pool]
  );

  // Auto → every tile (alphabetical); manual → the picked tiles, in the chosen order.
  const tilesToShow: ChildTileData[] = autoDisplay
    ? sortedPool
    : selectedIds.map((id) => byId[id]).filter(Boolean);

  // Type-aware navigation — mirrors CMSConsumerApplicationsGrid.handleNavigate.
  const navigateTile = (tile: ChildTileData) => {
    const { category_type, item_group, application } = tile;

    switch (category_type) {
      case 'application':
        if (item_group === '005' && application) {
          const apps = applicationMapping[application] || [application];
          const params = new URLSearchParams();
          apps.forEach((app) => params.append('application', app));
          navigate(`/item_group/005/application/${application.toLowerCase()}?${params.toString()}`);
        } else if (item_group && application) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`);
        }
        break;
      case 'item_group':
        if (item_group) navigate(`/item_group/${encodeURIComponent(item_group)}`);
        break;
      case 'collection':
        if (item_group && tile.collection) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/collection/${encodeURIComponent(tile.collection)}`);
        }
        break;
      case 'brand':
        if (tile.brand) navigate(`/brand/${encodeURIComponent(tile.brand)}`);
        break;
      case 'custom':
        navigate(`/${getCustomSlug(tile)}`);
        break;
      default:
        if (item_group && application) {
          navigate(`/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`);
        } else if (item_group) {
          navigate(`/item_group/${encodeURIComponent(item_group)}`);
        } else {
          console.warn('ItemGroupTiles (consumer): no navigation path for', tile);
        }
        break;
    }
  };

  const resolveImage = (tile: ChildTileData): string => {
    const override = customChildTiles?.[tile.category_id];
    if (override) return `${BASE_MEDIA_URL}${override}`;
    return tile.alternate_tile_media_link || tile.tile_display_media_single_link || '';
  };

  // Measure how many tiles fit in a row (a ResizeObserver on the tile container),
  // so every tile can be given the same fixed width. Callback ref so it
  // re-attaches if the container mounts later (once the async tile fetch resolves).
  const roRef = useRef<ResizeObserver | null>(null);
  const [fitCols, setFitCols] = useState(3);
  const [gridWidth, setGridWidth] = useState(0);
  const measureGrid = useCallback((el: HTMLDivElement | null) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w) {
        setFitCols(Math.max(1, Math.floor((w + TILE_GAP) / (MIN_TILE + TILE_GAP))));
        setGridWidth(w);
      }
    };
    update();
    roRef.current = new ResizeObserver(update);
    roRef.current.observe(el);
  }, []);

  // Phone = below the `sm` breakpoint, the same switch the home page tiles use to
  // go from two columns to one.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  // A single tile would sit half-empty, so the block only renders once there
  // are at least two tiles to fill the row (mirrors trade).
  if (tilesToShow.length < 2) return null;

  // Columns to size tiles by: as many as actually fit, but never fewer than three
  // when there's room for three — so one or two tiles stay three-up sized and
  // centre rather than stretching. On narrow screens where fewer than three fit,
  // the tiles fill as-is (bigger). Every tile gets this SAME fixed width, so a
  // wrapped last row stays equal-sized instead of the lone tile ballooning.
  // On a phone it is always one full-width tile per row, like the home page.
  const gap = isPhone ? PHONE_TILE_GAP : TILE_GAP;
  let cols: number;
  if (isPhone) {
    cols = 1;
  } else if (chosenCols) {
    cols = Math.max(1, Math.min(chosenCols, fitCols));
  } else {
    cols = Math.min(fitCols, tilesToShow.length);
    if (cols < 3 && fitCols >= 3) cols = 3;
  }
  cols = Math.max(1, cols);
  const tileWidth = `calc((100% - ${(cols - 1) * gap}px) / ${cols})`;

  const twoColTileHeight =
    !isPhone && chosenCols === 2 && cols === 2 && gridWidth
      ? Math.round(((gridWidth - 2 * TILE_GAP) / 3) * TILE_ASPECT_H_OVER_W)
      : undefined;

  // Label size: the home page's 40px, reduced only as far as needed for the
  // longest word to fit the tile (a 40px label clipped in the narrow tiles).
  const tilePx = gridWidth ? (gridWidth - (cols - 1) * gap) / cols : 0;
  const labelFontSize = tilePx
    ? Math.round(
        Math.min(
          LABEL_MAX_FONT,
          Math.max(LABEL_MIN_FONT, (tilePx - LABEL_SIDE_SPACE) / LABEL_LONGEST_WORD_EM)
        )
      )
    : LABEL_MAX_FONT;

  // Match trade (and the other CMS blocks): a set max width constrains and
  // centres the block; otherwise fall back to the default insets. On a phone the
  // tiles use the home page's side inset (its 16px Container padding) instead, so
  // they are exactly as wide as the home tiles whatever max width the block has.
  const containerSx = isPhone
    ? { mx: '16px' }
    : maxWidth
      ? { maxWidth, mx: 'auto' }
      : { ml: { xs: '5%', lg: '5rem' }, mr: { xs: '5%', lg: '5rem' } };

  return (
    <Box sx={containerSx}>
      <Box
        ref={measureGrid}
        sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: `${gap}px` }}
      >
        {tilesToShow.map((tile, index) => {
          const imageUrl = resolveImage(tile);
          const label = nameOverrides[tile.category_id] || tileName(tile);
          const hoverEnabled = tile.consumer_tile_hover_animation_switch ?? true;

          return (
            <Box
              key={tile.category_id || index}
              sx={{ flex: `0 0 ${tileWidth}`, width: tileWidth, maxWidth: tileWidth, minWidth: 0 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  width: '100%',
                  // Phone: the home page tile height. Otherwise the same 3:4 tile
                  // shape as the trade site.
                  ...(isPhone ? { height: `${PHONE_TILE_HEIGHT}px` } : twoColTileHeight ? { height: `${twoColTileHeight}px` } : { aspectRatio: '3/4' }),
                  overflow: 'hidden',
                  ...(hoverEnabled ? { '&:hover img': { transform: 'scale(1.03)' } } : {}),
                }}
                onClick={() => (clickAction === 'scroll' ? scrollToSection(tile) : navigateTile(tile))}
              >
                <img
                  src={imageUrl || '/placeholder-image.jpg'}
                  loading="eager"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: hoverEnabled ? 'transform 0.5s ease' : undefined,
                  }}
                  alt={label}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.36) 100%)',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 'lighter',
                      color: '#fff',
                      fontSize: `${labelFontSize}px`,
                      // Custom pages are named by hand in the hub: show that name as typed.
                      textTransform: tile.category_type === 'custom' ? 'none' : 'capitalize',
                      px: `${LABEL_SIDE_SPACE / 2}px`,
                      mb: { xs: 3, lg: 4 },
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ItemGroupTilesBlockRenderer;
